import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { FormField, inputClasses } from '../../components/ui/FormField';
import { buttonClasses } from '../../utils/buttonStyles';
import { track } from '../../utils/analytics';
type Status = 'idle' | 'submitting' | 'success';
export function WarrantyClaim() {
  const [form, setForm] = useState({
    email: '',
    order: '',
    purchase: 'individual',
    issue: 'doa',
    units: '1',
    details: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>('idle');
  const [ref, setRef] = useState('');
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setForm(f => ({
    ...f,
    [k]: e.target.value
  }));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = 'Enter the email used for your order.';
    if (!form.order.trim()) next.order = 'Order number is required as proof of purchase.';
    if (form.details.trim().length < 10) next.details = 'Please describe the issue (10+ characters).';
    setErrors(next);
    if (Object.keys(next).length) return;
    setStatus('submitting');
    track('warranty_claim_submit', {
      issue: form.issue,
      purchase: form.purchase
    });
    window.setTimeout(() => {
      setRef(`MFL-${Math.random().toString(36).slice(2, 8).toUpperCase()}`);
      setStatus('success');
    }, 800);
  };
  return <>
      <PageHeader title="Warranty Claim" lead="Tell us what happened. Covered defects are resolved with a replacement according to warranty terms." data-id="element-1365" data-mp="2b78m7dpnyi8a" />
      <div className="mx-auto grid max-w-site gap-14 px-5 py-16 md:px-8 md:py-24 lg:grid-cols-[1fr_1.5fr]" data-id="element-1366" data-mp="3mj11zbtizli4">
        <aside className="text-titanium" data-id="element-1367" data-mp="3rhudwzt5z4g6">
          <h2 className="font-display text-2xl font-semibold uppercase text-white" data-id="element-1368" data-mp="3rkyocex82wfa">Before you start</h2>
          <ul className="mt-4 list-disc space-y-2 pl-5" data-id="element-1369" data-mp="1nfb128dqgrml">
            <li data-id="element-1370" data-mp="1nerg0issdn4c">Have your order number ready.</li>
            <li data-id="element-1371" data-mp="21etwc0lg48hh">A photo of the issue helps us resolve it faster.</li>
            <li data-id="element-1372" data-mp="21czjzao8pop0">Accidental damage, misuse and normal wear are not covered.</li>
          </ul>
          <Link to="/warranty" className="mt-6 inline-block font-semibold text-ice hover:underline" data-id="element-1373" data-mp="1wgsa56sq3t1s">Read warranty information</Link>
        </aside>

        <div className="rounded-2xl border mf-hairline bg-navy p-6 md:p-10" data-id="element-1374" data-mp="13rtqu6cv6kuw">
          {status === 'success' ? <div role="status" className="py-10 text-center" data-id="element-1375" data-mp="1ih9gfj0nsk90">
              <CheckCircle2Icon className="mx-auto h-10 w-10 text-marine" data-id="element-1376" data-mp="1dphpv8hi6eiv" />
              <h2 className="mt-4 font-display text-3xl font-semibold uppercase" data-id="element-1377" data-mp="1dneq3wqjt70y">Claim submitted</h2>
              <p className="mt-2 text-titanium" data-id="element-1378" data-mp="pxu3n3v6rrmx">Reference <span className="font-semibold text-white" data-id="element-1379" data-mp="l1luq87l0uo7">{ref}</span>. We'll contact {form.email}.</p>
            </div> : <form noValidate onSubmit={submit} className="grid gap-6 md:grid-cols-2" data-id="element-1380" data-mp="2wmmfkdsngs0q">
              <FormField id="wc-email" label="Order email" error={errors.email} data-id="element-1381" data-mp="31fnu2koy2pjs">
                <input type="email" className={inputClasses} value={form.email} onChange={set('email')} autoComplete="email" data-id="element-1382" data-mp="2noafll5w8hvm" />
              </FormField>
              <FormField id="wc-order" label="Order number" error={errors.order} data-id="element-1383" data-mp="2rmq8g5onokgy">
                <input className={inputClasses} value={form.order} onChange={set('order')} placeholder="#1001" data-id="element-1384" data-mp="3ffggw4xcolmh" />
              </FormField>
              <FormField id="wc-purchase" label="What did you purchase?" data-id="element-1385" data-mp="36e8vuojuhhac">
                <select className={inputClasses} value={form.purchase} onChange={set('purchase')} data-id="element-1386" data-mp="36g3ajl4iwd5z">
                  <option value="individual" data-id="element-1387" data-mp="3bewm38091u3i">Individual lights</option>
                  <option value="sys4" data-id="element-1388" data-mp="1ux8ul3firs46">4-Light Starter System</option>
                  <option value="sys8" data-id="element-1389" data-mp="1zy6mi7qc00iv">8-Light Signature System</option>
                  <option value="sys12" data-id="element-1390" data-mp="2f7jse5rek9yn">12-Light Complete System</option>
                  <option value="sys24" data-id="element-1391" data-mp="2f9mrrgeg3fg1">24-Light Ultimate System</option>
                </select>
              </FormField>
              <FormField id="wc-issue" label="Issue type" data-id="element-1392" data-mp="25hqgt4r9fuue">
                <select className={inputClasses} value={form.issue} onChange={set('issue')} data-id="element-1393" data-mp="25i9xyj936f7d">
                  <option value="doa" data-id="element-1394" data-mp="21hgec22ofmka">Arrived defective</option>
                  <option value="power" data-id="element-1395" data-mp="2y6xooucltwsb">Won't power on / charge</option>
                  <option value="leak" data-id="element-1396" data-mp="2y41rj88kzc4x">Water ingress</option>
                  <option value="led" data-id="element-1397" data-mp="2t8mcwe2r1c7g">LED / color fault</option>
                  <option value="mount" data-id="element-1398" data-mp="2p57y1qqjr1vl">Mount base issue</option>
                  <option value="other" data-id="element-1399" data-mp="2p7keu0kkj672">Other</option>
                </select>
              </FormField>
              <FormField id="wc-units" label="Affected lights" data-id="element-1400" data-mp="2k8zbvtdxm8ex">
                <input type="number" min={1} className={inputClasses} value={form.units} onChange={set('units')} data-id="element-1401" data-mp="1ds5j1043ikdj" />
              </FormField>
              <FormField id="wc-photo" label="Photo (optional)" hint="JPG or PNG" data-id="element-1402" data-mp="3kete6rikar9n">
                <input type="file" accept="image/*" className="w-full text-sm text-titanium file:mr-4 file:rounded-md file:border-0 file:bg-white/10 file:px-4 file:py-2.5 file:text-white" data-id="element-1403" data-mp="3kcqfzk799rtu" />
              </FormField>
              <div className="md:col-span-2" data-id="element-1404" data-mp="20tvdtyii7n7">
                <FormField id="wc-details" label="Describe the issue" error={errors.details} data-id="element-1405" data-mp="21n6t88f1lbo">
                  <textarea rows={5} className={inputClasses} value={form.details} onChange={set('details')} data-id="element-1406" data-mp="6yvgsljeuj1u" />
                </FormField>
              </div>
              <div className="md:col-span-2" data-id="element-1407" data-mp="gy4ftst4unv2">
                <button type="submit" disabled={status === 'submitting'} className={buttonClasses('primary')} data-id="element-1408" data-mp="gzfljy6525ot">
                  {status === 'submitting' && <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-1409" data-mp="kxdbj37qgvt9" />}
                  Submit claim
                </button>
              </div>
            </form>}
        </div>
      </div>
    </>;
}